// src/components/ui.js
import React from 'react';

export function Spinner({ size = 'md' }) {
  const cls = size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-10 h-10' : 'w-6 h-6';
  return <div className={cls + " border-2 border-cosmic-300/20 border-t-cosmic-400 rounded-full animate-spin"} />;
}

export function LoadingScreen({ message = 'Loading...' }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-64 gap-4">
      <Spinner size="lg" />
      <p className="text-cosmic-400 text-sm">{message}</p>
    </div>
  );
}

export function Alert({ type = 'error', children }) {
  const styles = {
    error:   'bg-red-900/30 border-red-500/40 text-red-300',
    success: 'bg-green-900/30 border-green-500/40 text-green-300',
    info:    'bg-cosmic-800/40 border-cosmic-500/40 text-cosmic-200',
    warning: 'bg-yellow-900/30 border-yellow-500/40 text-yellow-300',
  };
  return (
    <div className={"flex items-start gap-2.5 px-4 py-3 rounded-xl border text-sm " + (styles[type] || styles.error)}>
      <span>{children}</span>
    </div>
  );
}

export function StatCard({ icon, label, value, sub }) {
  return (
    <div className="cosmic-card p-5 flex items-start gap-4">
      <div className="text-3xl">{icon}</div>
      <div>
        <p className="text-xs text-cosmic-400 uppercase tracking-widest mb-1">{label}</p>
        <p className="font-cinzel text-2xl font-bold text-white">{value}</p>
        {sub && <p className="text-xs text-cosmic-500 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

export function ScoreRing({ score, label, size = 100 }) {
  const r      = (size / 2) - 8;
  const circ   = 2 * Math.PI * r;
  const offset = circ - (score / 100) * circ;
  const color  = score >= 80 ? '#22c55e' : score >= 65 ? '#f59e0b' : score >= 50 ? '#8157ff' : '#ef4444';
  return (
    <div className="flex flex-col items-center gap-1">
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#1a0047" strokeWidth="8" />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="8"
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 1s ease' }} />
        <text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle"
          fill="white" fontSize={size * 0.22} fontFamily="Cinzel,serif" fontWeight="bold"
          transform={"rotate(90, " + (size/2) + ", " + (size/2) + ")"}>
          {score}%
        </text>
      </svg>
      {label && <p className="text-xs text-cosmic-400 text-center">{label}</p>}
    </div>
  );
}

export function PageHeader({ icon, title, subtitle }) {
  return (
    <div className="mb-8">
      <div className="flex items-center gap-3 mb-1">
        {icon && <span className="text-3xl">{icon}</span>}
        <h1 className="section-title">{title}</h1>
      </div>
      {subtitle && <p className="section-subtitle">{subtitle}</p>}
    </div>
  );
}

export function EmptyState({ icon, message, sub }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
      <span className="text-5xl opacity-40">{icon || '🌌'}</span>
      <p className="text-cosmic-300 font-semibold">{message}</p>
      {sub && <p className="text-cosmic-500 text-sm">{sub}</p>}
    </div>
  );
}
